import { yupResolver } from '@hookform/resolvers/yup';
import { ERROR_MESSAGES } from 'constants/validation-error-messages';
import { useUser } from 'hooks/useUser';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { fetchDepartmentsPaginated, getUserData, getWorkplaces, updateUser } from 'services/registration';
import { WorkplaceType } from 'views/registration/useRegistrationHook';
import * as yup from 'yup';

export interface FirstLoginFormValues {
    first_name: string;
    last_name: string;
    phone?: string;
    employee_position_name?: string;
    workplace_id?: number;
    department_id?: number | null;
    agreement: boolean;
}

const schema = yup.object({
    first_name: yup.string().required('registration.errors.required'),
    last_name: yup.string().required('registration.errors.required'),
    employee_position_name: yup.string().optional(),
    agreement: yup.boolean().oneOf([true], ERROR_MESSAGES.REQUIRED),
});

type WorkplaceOption = { label: string; value: WorkplaceType } | null;

export const useFirstLoginModal = () => {
    const { t } = useTranslation();
    const { userData, setUserData } = useUser();
    const location = useLocation();

    const MODAL_THRESHOLD = new Date('2026-04-19T00:00:00').getTime();

    const checkShouldShow = () => {
        try {
            const raw = localStorage.getItem('userData');
            if (!raw) return false;
            const data = JSON.parse(raw);
            if (!('previous_login_date' in data)) return false;
            if (!data.first_name || !data.last_name) return true;
            const date = data.previous_login_date;
            if (date === null) return true;
            return new Date(date).getTime() < MODAL_THRESHOLD;
        } catch {
            return false;
        }
    };

    const [isOpen, setIsOpen] = useState(checkShouldShow);

    useEffect(() => {
        setIsOpen(checkShouldShow());
    }, [location.pathname]);

    const readFromStorage = () => {
        try {
            const raw = localStorage.getItem('userData');
            if (!raw) return null;
            return JSON.parse(raw);
        } catch (e) {
            return null;
        }
    };

    const [hasName, setHasName] = useState(() => {
        const data = readFromStorage();
        return Boolean(data?.first_name && data?.last_name);
    });

    const initialWorkplaceOption: WorkplaceOption = userData?.workplace_name
        ? {
              label: userData.workplace_name,
              value: { ...userData, id: userData.workplace_id } as unknown as WorkplaceType,
          }
        : null;

    const [selectedWorkplace, setSelectedWorkplace] = useState<WorkplaceOption>(initialWorkplaceOption);
    const [selectedDepartment, setSelectedDepartment] = useState<{ label: string; value: number } | null>(null);

    const [hasDepartmentId, setHasDepartmentId] = useState(() => {
        const data = readFromStorage();
        return !!data?.department_id;
    });

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        setError,
        setValue,
    } = useForm<FirstLoginFormValues>({
        resolver: yupResolver(schema as any),
        defaultValues: {
            first_name: userData?.first_name || '',
            last_name: userData?.last_name || '',
            phone: userData?.phone || '',
            employee_position_name: userData?.employee_position_name || 'stanowisko',
            workplace_id: userData?.workplace_id,
            department_id: null,
        },
    });

    const currentWorkplaceId = selectedWorkplace?.value?.id;

    const needsDepartment = !hasDepartmentId && !!currentWorkplaceId;

    const loadDepartmentOptions = async (search: string, _: any, { page }: { page: number }) => {
        if (!currentWorkplaceId) return { options: [], hasMore: false, additional: { page: 1 } };
        const res = await fetchDepartmentsPaginated(currentWorkplaceId, page);
        return {
            options: res.data.map((d: any) => ({ label: d.name, value: d.id })),
            hasMore: res.current_page < res.last_page,
            additional: { page: page + 1 },
        };
    };

    const mutation = useMutation(updateUser, {
        onSuccess: async () => {
            toast.success(t('registration.successUpdated'));
            try {
                const res = await getUserData();
                setUserData({ ...res.data, previous_login_date: new Date('2099-01-01').toISOString() });
            } catch {
                setUserData({ ...userData, previous_login_date: new Date('2099-01-01').toISOString() });
            }
            setIsOpen(false);
        },
        onError: (error: any) => {
            const formErrors = error?.response?.data?.errors;
            if (formErrors) {
                Object.entries(formErrors).forEach(([key, value]) => {
                    setError(key as keyof FirstLoginFormValues, {
                        message: (value as string[])?.[0],
                    });
                });
            }
        },
    });

    const onSubmit = ({ agreement: _, department_id, ...data }: FirstLoginFormValues) => {
        const payload: any = { ...data };
        
        if (department_id != null) payload.department_id = department_id;
        console.log(payload);

        mutation.mutate(payload);
    };

    const loadWorkplaceOptions = async (search: string, _: any, { page }: { page: number }) => {
        const res = await getWorkplaces({ name: search || undefined, page });
        return {
            options: res.data.map((w: WorkplaceType) => ({
                label: w.workplace_name,
                value: w,
            })),
            hasMore: res.current_page < res.last_page,
            additional: { page: page + 1 },
        };
    };

    const handleWorkplaceChange = (option: WorkplaceOption) => {
        setSelectedWorkplace(option);
        setSelectedDepartment(null);
        setValue('department_id', null);
        if (!option) return;
        const w = option.value;
        if (w.id) {
            setValue('workplace_id', w.id);
        }
    };

    const handleDepartmentChange = (option: { label: string; value: number } | null, fieldOnChange: (val: number | null) => void) => {
        setSelectedDepartment(option);
        fieldOnChange(option?.value ?? null);
    };

    useEffect(() => {
        if (isOpen) document.body.style.overflowY = 'hidden';
        else document.body.style.overflowY = 'auto';
        return () => {
            document.body.style.overflowY = 'auto';
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        try {
            const raw = localStorage.getItem('userData');
            if (!raw) return;
            const data = JSON.parse(raw);

            setHasName(Boolean(data.first_name && data.last_name));
            setHasDepartmentId(Boolean(data.department_id));
            setValue('first_name', data.first_name || '');
            setValue('last_name', data.last_name || '');
            setValue('phone', data.phone || '');
            setValue('employee_position_name', data.employee_position_name || 'stanowisko');
            if (data.workplace_id) setValue('workplace_id', data.workplace_id);
            if (data.workplace_name) {
                setSelectedWorkplace({
                    label: data.workplace_name,
                    value: { ...data, id: data.workplace_id } as unknown as WorkplaceType,
                });
            }
        } catch (e) { /* ignore parse errors */ }
    }, [isOpen]);

    return {
        t,
        isOpen,
        hasName,
        register,
        handleSubmit,
        errors,
        onSubmit,
        loadWorkplaceOptions,
        handleWorkplaceChange,
        selectedWorkplace,
        selectedDepartment,
        needsDepartment,
        loadDepartmentOptions,
        handleDepartmentChange,
        control,
    };
};
